import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  XMarkIcon,
  ArrowPathIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { alertApi } from '../lib/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { PageHeader } from '../components/ui';
import { useTeamId } from '../hooks/useTeamId';
import ActiveBankNotice from '../components/ActiveBankNotice';

type Regulator = 'FRB' | 'FDIC';

export default function AlertUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [regulator, setRegulator] = useState<Regulator>('FRB');
  const [runDate, setRunDate] = useState<string>('');
  // Ingest now runs out-of-band on the server (returns 202); we poll the file's status.
  const [isPolling, setIsPolling] = useState(false);

  // Bind to the ACTIVE team (the global TeamSwitcher / current tenant) rather than a
  // per-feature picker — a multi-bank user can't accidentally ingest under the wrong bank.
  const { teamId: activeTeamId } = useTeamId();

  const { data: recentData, refetch: refetchRecent } = useQuery({
    queryKey: ['alert-files-recent'],
    queryFn: async () => {
      const response = await alertApi.getFiles({ limit: 5 });
      return response.data;
    },
  });
  const files = recentData?.files || [];

  // Poll the file's status until the out-of-band ingest finishes (COMPLETED) or fails.
  const pollUntilDone = useCallback(
    async (alertFileId: string) => {
      setIsPolling(true);
      const startedAt = Date.now();
      const MAX_MS = 10 * 60 * 1000; // 10-minute ceiling for very large books
      // Tolerate transient failures (e.g. a deploy/restart blip mid-ingest) instead of
      // abandoning the whole poll after a single failed request.
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 10; // ~15s of back-to-back failures before giving up
      try {
        while (Date.now() - startedAt < MAX_MS) {
          await new Promise((r) => setTimeout(r, 1500));
          let f;
          try {
            f = (await alertApi.getFile(alertFileId)).data?.file;
            consecutiveErrors = 0;
          } catch {
            if (++consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              toast.error('Lost connection while ingesting — check Recent Files in a moment.');
              return;
            }
            continue; // transient — keep polling
          }
          if (!f) continue;
          if (f.status === 'COMPLETED') {
            toast.success(
              `Ingested ${Number(f.totalLoans || 0).toLocaleString()} loans (${Number(
                f.consumerLoans || 0,
              ).toLocaleString()} consumer).`,
            );
            setFile(null);
            refetchRecent();
            navigate(`/alert/files/${alertFileId}`);
            return;
          }
          if (f.status === 'FAILED') {
            toast.error(f.errorMessage || 'ALERT ingest failed during processing.');
            refetchRecent();
            return;
          }
          // UPLOADED / PARSING / PROXYING — keep waiting.
        }
        toast.error('Ingest is taking longer than expected — check Recent Files in a moment.');
      } catch {
        toast.error('Lost track of the ingest — check Recent Files in a moment.');
      } finally {
        setIsPolling(false);
        refetchRecent();
      }
    },
    [navigate, refetchRecent],
  );

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      return alertApi.upload(
        file,
        { regulator, runDate: runDate || undefined },
        activeTeamId || undefined,
      );
    },
    onSuccess: (response) => {
      const { alertFileId, deduplicated, status, rollups } = response.data;
      // Dedup (or an already-complete file) returns the rollups inline — open it immediately.
      if (deduplicated || status === 'COMPLETED') {
        toast.success(
          deduplicated
            ? 'This file was already ingested — opening the existing analysis.'
            : `Ingested ${Number(rollups?.totalLoans || 0).toLocaleString()} loans (${Number(
                rollups?.consumerLoans || 0,
              ).toLocaleString()} consumer).`,
        );
        setFile(null);
        refetchRecent();
        navigate(`/alert/files/${alertFileId}`);
        return;
      }
      // 202 Accepted — processing out-of-band. Poll the file's status until it's done.
      toast.success('Upload received — ingesting & proxying in the background…');
      refetchRecent();
      void pollUntilDone(alertFileId);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'ALERT ingest failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertApi.deleteFile(id),
    onSuccess: () => {
      toast.success('File deleted — you can re-upload a fresh copy.');
      refetchRecent();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Could not delete the file.');
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.dat'],
      'text/plain': ['.dat', '.txt'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxSize: 100 * 1024 * 1024,
    multiple: false,
    maxFiles: 1,
  });

  const handleUpload = () => {
    if (!file) {
      toast.error('Please select an ALERT file');
      return;
    }
    uploadMutation.mutate();
  };

  const isProcessing = uploadMutation.isPending || isPolling;

  return (
    <div>
      <PageHeader
        title="ALERT / ILDR Analysis"
        description="Upload a loan trial balance for fair-lending analysis beyond HMDA — consumer, small business, and small farm loans, with proxied sex/ethnicity."
      />

      {/* Active bank — ALERT ingests under the active tenant (switch via the sidebar). */}
      <ActiveBankNotice className="mb-6" />

      {/* Analysis options */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="alert-regulator" className="block text-sm font-medium text-gray-700 mb-2">
            Proxy methodology
          </label>
          <select
            id="alert-regulator"
            value={regulator}
            onChange={(e) => setRegulator(e.target.value as Regulator)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
          >
            <option value="FRB">FRB (joints excluded from F-vs-M)</option>
            <option value="FDIC">FDIC (any-male-present = male)</option>
          </select>
        </div>
        <div>
          <label htmlFor="alert-rundate" className="block text-sm font-medium text-gray-700 mb-2">
            Run date <span className="text-gray-400 font-normal">(optional — defaults to today)</span>
          </label>
          <input
            id="alert-rundate"
            type="date"
            value={runDate}
            onChange={(e) => setRunDate(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Sets the rolling 12-month Focal Point window (ends at the month-end on or before this date).
          </p>
        </div>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
          isDragActive
            ? 'border-ocean-500 bg-ocean-50 shadow-lg'
            : 'border-gray-300 hover:border-ocean-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <TableCellsIcon className={`mx-auto h-12 w-12 transition-colors ${isDragActive ? 'text-ocean-500' : 'text-gray-400'}`} />
        <p className="mt-2 text-sm font-medium text-navy-900">
          {isDragActive ? 'Drop the ALERT file here' : 'Drag & drop an ALERT file here, or click to select'}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Fixed-width <span className="font-mono">.dat</span> (82-field ILDR layout) or Excel <span className="font-mono">.xlsx</span> — max 100MB
        </p>
      </div>

      {/* Selected file */}
      <AnimatePresence>
        {file && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6"
          >
            <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-5 w-5 text-ocean-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex gap-4">
              <button
                onClick={handleUpload}
                disabled={isProcessing}
                className="flex-1 bg-ocean-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-600 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              >
                {isProcessing ? (
                  <span className="inline-flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Ingesting & proxying…
                  </span>
                ) : (
                  'Ingest & Analyze'
                )}
              </button>
              <button
                onClick={() => setFile(null)}
                className="px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                Clear
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PII note */}
      <div className="mt-12 bg-ocean-50 border border-ocean-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-navy-900 mb-2 inline-flex items-center gap-2">
          <ShieldCheckIcon className="h-4 w-4 text-ocean-600" />
          What happens after upload?
        </h3>
        <ul className="text-sm text-navy-700 space-y-1">
          <li>&#8226; The Taxpayer ID / SSN is dropped at the parse boundary — never stored.</li>
          <li>&#8226; Each loan is classified (SB / SF / consumer / residential) from its FFIEC RC-C code.</li>
          <li>&#8226; Borrower names drive a sex/ethnicity proxy (FRB + FDIC), reproducing the FRB Fair Lending Tool.</li>
          <li>&#8226; Names and addresses are encrypted at rest (AES-256-GCM); proxy lookups run on blind indexes.</li>
          <li>&#8226; Consumer 6-series loans in the 12-month window feed the rate-disparity analysis.</li>
        </ul>
      </div>

      {/* Recent files */}
      {files.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-navy-900">Recent Files</h2>
          </div>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {files.map((f: any) => (
                <li
                  key={f.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/alert/files/${f.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{f.fileName}</p>
                        <p className="text-xs text-gray-500">
                          {f.totalLoans?.toLocaleString()} loans &middot; {f.consumerLoans?.toLocaleString()} consumer &middot;{' '}
                          {f.regulator} &middot; {formatDistanceToNow(new Date(f.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {f.status && f.status !== 'COMPLETED' && (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            f.status === 'FAILED'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-ocean-100 text-ocean-700'
                          }`}
                        >
                          {f.status === 'FAILED' ? (
                            'Failed'
                          ) : (
                            <>
                              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                              Processing…
                            </>
                          )}
                        </span>
                      )}
                      {f.sexAnalysisBlocked && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                          Sex gated
                        </span>
                      )}
                      {f.quarantinedLoans > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {f.quarantinedLoans.toLocaleString()} quarantined
                        </span>
                      )}
                      <button
                        title="Delete this file"
                        aria-label={`Delete ${f.fileName}`}
                        disabled={deleteMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              `Delete "${f.fileName}" and all its ingested loans? This cannot be undone.`,
                            )
                          ) {
                            deleteMutation.mutate(f.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
